import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useBooleanState } from '@cian/react-utils';
import { ISuggestionItem as IUIKitSuggestionItem, Input, Suggest, useDeviceType, Button } from '@cian/ui-kit';

import { IconActionSearch16, IconActionSearch24 } from '@cian/ui-kit-design-tokens/icons';

import { useEmptyListText, useSuggestions, useDebouncedValueEffect } from './hooks';
import { RightAdornmentContainer } from './parts';
import { trackPromoSearchClickAction } from '../../actions/analytics';
import { promosApplyFiltersRequestAction } from '../../actions/filters';
import { mergeJsonQueryAction } from '../../actions/jsonQuery';
import { suggestionsReset } from '../../actions/suggestions';
import { SearchSuggestAdornment } from '../../components/SearchSuggest';
import { TThunkDispatch } from '../../types/redux/thunk';
import { ISuggestionItem as ISuggestion } from '../../types/suggestions';
import { trackPromoSearchSelect } from '../../utils/analytics';

interface ISearchSuggestContainerProps {
  isFixedHeaderShown?: boolean;
}

export const SearchSuggestContainer = ({ isFixedHeaderShown }: ISearchSuggestContainerProps) => {
  const { state: open, setFalse: closeSuggest, setTrue: openSuggest } = useBooleanState();
  const [value, setValue] = React.useState('');

  const suggestions = useSuggestions(value);
  const isDebouncing = useDebouncedValueEffect(value, openSuggest, closeSuggest);
  const emptyListText = useEmptyListText(value, isDebouncing);

  const deviceType = useDeviceType();
  const dispatch: TThunkDispatch = useDispatch();

  const clearValue = React.useCallback(() => {
    dispatch(suggestionsReset());
    setValue('');
  }, [dispatch]);

  const clearValueAndClose = React.useCallback(() => {
    clearValue();
    closeSuggest();
  }, [clearValue, closeSuggest]);

  const handleInputClick = React.useCallback(() => {
    if (deviceType !== 'desktop') {
      openSuggest();
    }

    dispatch(trackPromoSearchClickAction());
  }, [dispatch, deviceType, openSuggest]);

  const handleItemSelect = React.useCallback(
    (item: IUIKitSuggestionItem<ISuggestion>) => {
      clearValueAndClose();
      dispatch(mergeJsonQueryAction(item.value.jsonQuery));
      dispatch(promosApplyFiltersRequestAction());
      trackPromoSearchSelect();
    },
    [dispatch, clearValueAndClose],
  );

  const showInput = React.useMemo(() => {
    return deviceType === 'desktop' || open || !isFixedHeaderShown;
  }, [deviceType, open, isFixedHeaderShown]);

  return (
    <Suggest
      suggestions={suggestions}
      emptyListText={emptyListText}
      open={open}
      onClose={clearValueAndClose}
      onSelect={handleItemSelect}
      showEmptySuggest
      title="Поиск акций"
    >
      {showInput && (
        <Input
          size="M"
          placeholder="Найти ЖК, банк, застройщика"
          value={value}
          onChange={(_event, value) => setValue(value)}
          onClick={handleInputClick}
          leftAdornment={<SearchSuggestAdornment icon={<IconActionSearch16 />} />}
          rightAdornment={
            <RightAdornmentContainer
              suggestionsLength={suggestions.length}
              valueLength={value.length}
              onClearClick={clearValue}
            />
          }
          data-testid="SearchSuggestInput"
        />
      )}

      {!showInput && (
        <Button
          beforeIcon={<IconActionSearch24 color="icon-main-default" />}
          theme="fill_white_primary"
          onClick={openSuggest}
          aria-label="Поиск акций"
          data-testid="SearchSuggestButton"
          size="XS"
        />
      )}
    </Suggest>
  );
};
